/**
 * Imperatively own the page's favicon link.
 *
 * Why this exists at all: the platform's static index.html ships an unmanaged
 * `<link rel="icon" href="/favicon.ico">` (auto-injected by rsbuild), AND
 * browsers auto-fetch `/favicon.ico` from the origin and cache that response
 * per-origin. Either of those will out-rank a Helmet-managed favicon link in
 * the tab UI. To make a custom favicon actually win, we have to: (1) remove
 * every competing icon link the platform/bundler added, and (2) install our
 * own with explicit type+sizes so browsers prefer it over the cached `.ico`.
 *
 * Idempotency: the helper marks its own link with `data-managed-favicon` and
 * skips work when an authoritative link with the same href is already in
 * place. That makes it safe to call from multiple places (early-paint hook
 * before React mounts, `<Head>` useEffect, nested `<Head>` instances under
 * the same tree) without churning the DOM.
 */
export function setManagedFavicon(href: string): () => void {
	if (typeof document === 'undefined' || !href) {
		return () => {};
	}

	const existing = document.querySelector<HTMLLinkElement>(
		'link[data-managed-favicon][rel="icon"]',
	);
	if (existing?.getAttribute('href') === href) {
		return () => existing.remove();
	}

	document
		.querySelectorAll(
			'link[rel="icon"]:not([data-managed-favicon]), link[rel="shortcut icon"], link[rel="apple-touch-icon"], link[rel="apple-touch-icon-precomposed"]',
		)
		.forEach((el) => el.remove());

	const link = existing ?? document.createElement('link');
	link.setAttribute('rel', 'icon');
	link.setAttribute('type', 'image/png');
	link.setAttribute('sizes', 'any');
	link.setAttribute('href', href);
	link.setAttribute('data-managed-favicon', '');
	if (!existing) {
		document.head.appendChild(link);
	}

	return () => link.remove();
}
